import React, {
  useEffect, useRef, useState, useCallback,
} from 'react';
import {
  Box, Text, VStack, HStack, Button, ButtonText, ButtonSpinner, Center, Pressable, Icon, ButtonIcon,
} from '@gluestack-ui/themed';
import {
  RefreshCcw, Trash2, Upload, Sparkles,
} from 'lucide-react-native';
import { Image } from 'expo-image';
import { Platform } from 'react-native';
import { useFilePicker } from '@/common/hooks/lecturer/useFilePicker';
import { useDialog } from '@/common/context/DialogContext';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import { useRoute, RouteProp } from '@react-navigation/native';
import type { LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';

interface ModuleImageFormProps {
  readonly?: boolean;
  compact?: boolean;
}

const ModuleImageForm: React.FC<ModuleImageFormProps> = ({
  readonly = false,
  compact = false,
}) => {
  const { pickFile } = useFilePicker();
  const { showDialog } = useDialog();
  const { reportApiError } = useApiErrorReporter();
  const route = useRoute<RouteProp<LecturerRouteParamList, 'ModuleEditor'>>();
  const moduleId = route.params?.moduleId;
  const version = route.params?.version ?? '0.0';

  const {
    module, uploadImage, deleteImage, generateImage,
    uploadImageMutation, deleteImageMutation, generateImageMutation,
  } = useLecturerModuleData(moduleId, version, undefined);

  const [dragCounter, setDragCounter] = useState(0);
  const isDragging = dragCounter > 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dropRef = useRef<any>(null);

  // Dimensions based on compact mode - match description height
  const imageHeight = compact ? 140 : 200;
  const emptyHeight = compact ? 140 : 160;
  const maxWidth = compact ? 300 : 720;

  const uploadModuleImage = useCallback((file: File) => {
    if (!file) return;
    if (file.size > 1000000) {
      showDialog({
        title: 'File too large',
        message: 'The image file is too large. Please upload an image that is less than 1MB.',
        buttons: [{ text: 'OK', action: 'positive' }],
      });
      return;
    }
    // check for allowed image extensions (case-insensitive)
    if (!file.name.match(/\.(jpe?g|png)$/i)) {
      showDialog({
        title: 'Invalid file type',
        message: 'Please upload a valid image file (jpg, jpeg, png).',
        buttons: [{ text: 'OK', action: 'positive' }],
      });
      return;
    }

    uploadImage(file)
      .catch((error: Error) => {
        reportApiError(error, 'Upload Image');
      });
  }, [uploadImage, reportApiError, showDialog]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !dropRef.current || readonly) {
      return undefined;
    }

    const el = dropRef.current;
    if (!el) return undefined;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => prev + 1);
    };

    const onDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter((prev) => Math.max(0, prev - 1));
    };

    const onDrop = async (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragCounter(0);

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        const file = e.dataTransfer.files[0];
        uploadModuleImage(file);
      }
    };

    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragenter', onDragEnter);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);

    return () => {
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('dragenter', onDragEnter);
      el.removeEventListener('dragleave', onDragLeave);
      el.removeEventListener('drop', onDrop);
    };
  }, [readonly, uploadModuleImage]);

  const handleRemoveImage = () => {
    showDialog({
      title: 'Remove Image',
      message: 'Do you want to remove the image for this module?',
      buttons: [
        { text: 'Cancel', variant: 'outline', action: 'secondary' },
        {
          text: 'Remove Image',
          action: 'negative',
          onPress: async () => {
            try {
              await deleteImage();
            } catch (error) {
              reportApiError(error as Error, 'Delete Image');
            }
          },
        },
      ],
    });
  };

  const handleUpdateImage = () => {
    pickFile(uploadModuleImage);
  };

  const handleAutoGenerate = async () => {
    try {
      await generateImage();
    } catch (error) {
      reportApiError(error as Error, 'Generate Image');
    }
  };

  // module image should not flicker, so we memoize the module image
  const moduleImageUrl = React.useMemo(() => module?.imageUrl, [module?.imageUrl]);

  return (
    <Box ref={dropRef}>
      <Box position="relative" maxWidth={maxWidth}>
        {moduleImageUrl ? (
          <Box
            borderRadius="$sm"
            h={imageHeight}
            overflow="hidden"
            borderWidth={isDragging ? 2 : 0}
            borderColor="$brandBase"
            borderStyle="dashed"
          >
            <Box
              width="100%"
              height="100%"
              sx={{
                _web: {
                  opacity: isDragging || generateImageMutation.isPending ? 0.3 : 1,
                },
              }}
            >
              <Image
                source={{ uri: moduleImageUrl }}
                alt="Module Image"
                contentFit="cover"
                // eslint-disable-next-line react-native/no-inline-styles
                style={{
                  width: '100%',
                  height: '100%',
                }}
                transition={0}
              />
            </Box>
            {isDragging && (
              <Center position="absolute" top={0} left={0} right={0} bottom={0}>
                <Icon as={Upload} size="xl" color="$brandStrong" />
              </Center>
            )}
            {!readonly && !isDragging && !generateImageMutation.isPending && (
              <HStack position="absolute" top={8} right={8} space="xs">
                <Pressable
                  testID="module-image-generate-button-exists"
                  onPress={handleAutoGenerate}
                  borderRadius="$xs"
                  bg="rgba(255,255,255,0.9)"
                  p="$1"
                  opacity={generateImageMutation.isPending ? 0.5 : 1}
                  disabled={generateImageMutation.isPending || readonly}
                >
                  <Icon as={Sparkles} size="2xs" color="$textSubtle" />
                </Pressable>
                <Pressable
                  onPress={handleUpdateImage}
                  borderRadius="$xs"
                  bg="rgba(255,255,255,0.9)"
                  p="$1"
                  opacity={uploadImageMutation.isPending ? 0.5 : 1}
                  disabled={uploadImageMutation.isPending || readonly}
                >
                  <Icon as={RefreshCcw} size="2xs" color="$textSubtle" />
                </Pressable>
                <Pressable
                  onPress={handleRemoveImage}
                  borderRadius="$xs"
                  bg="rgba(255,255,255,0.9)"
                  p="$1"
                  opacity={deleteImageMutation.isPending ? 0.5 : 1}
                  disabled={deleteImageMutation.isPending || readonly}
                >
                  <Icon as={Trash2} size="2xs" color="$textSubtle" />
                </Pressable>
              </HStack>
            )}
          </Box>
        ) : (
          <Center
            h={emptyHeight}
            borderRadius="$sm"
            bg={isDragging ? '$bgBrandSubtle' : '$bgSubtle'}
            borderWidth={isDragging ? 2 : 1}
            borderColor={isDragging ? '$brandBase' : '$borderLight'}
            borderStyle="dashed"
            position="relative"
          >
            <VStack space="xs" alignItems="center">
              {isDragging ? (
                <>
                  <Icon as={Upload} size="lg" color="$brandStrong" />
                  <Text size="2xs" color="$brandStrong" fontWeight="bold">
                    Drop image here
                  </Text>
                </>
              ) : (
                <VStack space="xs" alignItems="center" px="$4">
                  <Text size="2xs" color="$textSubtle" textAlign="center" mb="$1">
                    {readonly ? 'No cover image' : 'Drop an image here or use buttons'}
                  </Text>
                  {!readonly && (
                    <HStack space="sm">
                      <Button
                        size="xs"
                        variant="outline"
                        action="secondary"
                        onPress={handleUpdateImage}
                        isDisabled={uploadImageMutation.isPending || generateImageMutation.isPending}
                      >
                        <ButtonIcon as={Upload} size="2xs" mr="$1" />
                        {uploadImageMutation.isPending ? <ButtonSpinner /> : <ButtonText>Upload</ButtonText>}
                      </Button>
                      <Button
                        testID="module-image-generate-button"
                        size="xs"
                        variant="outline"
                        action="secondary"
                        onPress={handleAutoGenerate}
                        isDisabled={uploadImageMutation.isPending || generateImageMutation.isPending}
                      >
                        <ButtonIcon as={Sparkles} size="2xs" mr="$1" />
                        <ButtonText>Generate</ButtonText>
                      </Button>
                    </HStack>
                  )}
                </VStack>
              )}
            </VStack>
          </Center>
        )}
        {generateImageMutation.isPending && (
          <Center
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(255,255,255,0.8)"
            borderRadius="$sm"
          >
            <VStack space="sm" alignItems="center">
              <ButtonSpinner size="large" />
              <Text size="xs" fontWeight="$bold">Generating AI Image...</Text>
            </VStack>
          </Center>
        )}
      </Box>
    </Box>
  );
};

export default ModuleImageForm;
